import './SearchTable.css';

import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Pagination } from '../pagination/pagination';
import { SearchInput } from './SearchInput';
import { Translates } from '@/services/repository/i18n';
import { useModal } from '../modal/Modal';
import { isNFCSupported, NFCReaderPopup } from '../nfc/reader';

export interface PageQuery {
  pageSize: number;
  pageNumber: number;
  search: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface PageResponse<T> {
  pageSize: number;
  pageNumber: number;
  totalPages: number;
  data: T[] | undefined;
}

export interface SearchTableParams<T> {
  fetch: (q: PageQuery) => Promise<PageResponse<T>>;
  toolbox?: () => React.ReactElement;
  header?: (query: PageQuery, onSort: (field: string) => void) => React.ReactElement;
  row: (row: T, state: PageResponse<T>, setState: React.Dispatch<React.SetStateAction<PageResponse<T> | undefined>>) => React.ReactElement;
  pageQuery: PageQuery;
  defaultSortBy?: string;
  translates: Translates
  className?: string;
  searchByNFC?: boolean
}

export interface SearchTableRef {
  refresh: () => void;
}

export const SearchTable = forwardRef<SearchTableRef, SearchTableParams<any>>(
  <T,>(
    { row, fetch, pageQuery: initialPageQuery, defaultSortBy, translates, toolbox, header, className, searchByNFC }: SearchTableParams<T>,
    ref: React.Ref<SearchTableRef>
  ) => {
    // Use local state for pageQuery
    const [currentQuery, setCurrentQuery] = useState<PageQuery>(() => ({
      ...initialPageQuery,
      sortBy: initialPageQuery.sortBy || defaultSortBy,
      sortDirection: initialPageQuery.sortDirection || 'asc',
    }));
    const [resp, setResp] = useState<PageResponse<T>>();
    const [error, setError] = useState<string>();
    const respRef = React.useRef<PageResponse<T> | undefined>(resp);
    const { openModal } = useModal()
    const bodyWrapRef = React.useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      respRef.current = resp;
    }, [resp]);

    // Scroll to top on page change (optionally include search/pageSize too)
    useEffect(() => {
      if(!bodyWrapRef.current) {
        return
      }
      bodyWrapRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentQuery.pageNumber]);

    // Update response when currentQuery changes
    useEffect(() => {
      let active = true;
      fetch(currentQuery).then(
        (response) =>
          active && setResp(
            response || {
              pageSize: currentQuery.pageSize,
              pageNumber: 1,
              totalPages: 0,
              data: [],
            }
          ),
        (error) => { if (active) setError(error); }
      );
      return () => { active = false; };
    }, [currentQuery, fetch]);

    // When props change, reset local state accordingly
    useEffect(() => {
      setCurrentQuery(q => ({ ...initialPageQuery, sortBy: q.sortBy || initialPageQuery.sortBy || defaultSortBy, sortDirection: q.sortDirection || initialPageQuery.sortDirection || 'asc' }));
    }, [initialPageQuery, defaultSortBy]);

    // For imperative refresh (reload current query)
    useImperativeHandle(ref, () => ({
      refresh: () => setCurrentQuery({ ...currentQuery }),
    }));

    // Handlers
    const handleSearchDebounced = React.useCallback(
      (search: string) => {
        setCurrentQuery((q) => ({ ...q, pageNumber: 1, search }));
      },
      []
    );

    // To update immediately as you type (for SearchInput)
    const handleSearchChange = React.useCallback((v: string) => {
      setCurrentQuery((q) => ({ ...q, search: v }));
    }, []);

    // Pagination
    const handlePageChange = (pagination: { pageSize: number; pageNumber: number }) => {
      setCurrentQuery((q) => ({ ...q, ...pagination }));
    };

    const handleSort = (field: string) => {
      setCurrentQuery(q => ({
        ...q,
        pageNumber: 1,
        sortBy: field,
        sortDirection: q.sortBy === field && q.sortDirection === 'asc' ? 'desc' : 'asc',
      }));
    };

    if (error) return <div className="main-search-error">{error}</div>;

    if (!resp) return <div className="spinner"></div>;

    return (
      <div className={className || "main-search-table"}>
        <div className="toolbox">
          <div className="left">{toolbox && toolbox()}</div>
          <div className="right">
            <SearchInput
              value={currentQuery.search}
              onChange={handleSearchChange}
              onDebouncedChange={handleSearchDebounced}
              translates={translates}
            />
            {searchByNFC && isNFCSupported() ? <div className='nfc-box'>
              <div onClick={() => openModal((props) => NFCReaderPopup({
                ...props, onRead: (cardId) => {
                  setCurrentQuery({
                    ...currentQuery,
                    search: cardId
                  })
                }, translates
              }))} className='icon-button'>
                <i className='svgfont-nfc' />
              </div>
            </div> : null}
          </div>
        </div>

        {resp.data?.length ? <Pagination
          translates={translates}
          pageQuery={{
            pageSize: resp.pageSize,
            pageNumber: resp.pageNumber,
            totalPages: resp.totalPages,
          }}
          onChange={handlePageChange}
        /> : null}

        <div className="search-body-wrap" ref={bodyWrapRef}>
          <div className="search-body">
            {header && <div className="row header">{header(currentQuery, handleSort)}</div>}
            {resp.data?.map((item) => (
              <div className="row" key={(item as any).id}>
                {row(item, resp, setResp)}
              </div>
            ))}
          </div>
        </div>

        {!resp.data?.length && <div className="empty-search-table-message">
          {translates.for("elements.search.empty_table")}
        </div> }
        
        {resp.data?.length ? <Pagination
          translates={translates}
          pageQuery={{
            pageSize: resp.pageSize,
            pageNumber: resp.pageNumber,
            totalPages: resp.totalPages,
          }}
          onChange={handlePageChange}
        /> : null}
      </div>
    );
  }
);
